<script setup>
import { computed, ref } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { BaseTableCell, BaseTableRow } from 'dashboard/components-next/table';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';

const props = defineProps({
  invoices: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  errorCode: { type: String, default: '' },
  activeInvoiceId: { type: [String, Number], default: null },
  invoiceDetailsById: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'toggleInvoice',
  'retryDescription',
  'retry',
  'close',
]);

const { t } = useI18n();
const dialogRef = ref(null);

const headers = computed(() => [
  t('CONVERSATION_SIDEBAR.ERP.INVOICES.ID'),
  t('CONVERSATION_SIDEBAR.ERP.INVOICES.ISSUED_ON'),
  t('CONVERSATION_SIDEBAR.ERP.INVOICES.DUE_ON'),
  t('CONVERSATION_SIDEBAR.ERP.INVOICES.TOTAL'),
  '',
]);

const errorMessageFor = code => {
  const messages = {
    payment_promise_already_exists: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.PAYMENT_PROMISE_ALREADY_EXISTS'
    ),
    invoice_not_found: t('CONVERSATION_SIDEBAR.ERP.ERRORS.INVOICE_NOT_FOUND'),
    invalid_request: t('CONVERSATION_SIDEBAR.ERP.ERRORS.INVALID_REQUEST'),
    authentication_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.AUTHENTICATION_ERROR'
    ),
    authorization_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.AUTHORIZATION_ERROR'
    ),
    configuration_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.CONFIGURATION_ERROR'
    ),
    timeout: t('CONVERSATION_SIDEBAR.ERP.ERRORS.TIMEOUT'),
  };

  return code
    ? messages[code] || t('CONVERSATION_SIDEBAR.ERP.ERRORS.PROVIDER_ERROR')
    : '';
};

const errorMessage = computed(() => errorMessageFor(props.errorCode));

const displayDate = value => format(parseISO(value), 'dd/MM/yyyy');
const displayTotal = value => Number(value).toFixed(2);

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="t('CONVERSATION_SIDEBAR.ERP.INVOICES.TITLE')"
    :description="t('CONVERSATION_SIDEBAR.ERP.INVOICES.DESCRIPTION')"
    :cancel-button-label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.CLOSE')"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div
      v-if="isLoading"
      class="flex min-h-40 flex-col items-center justify-center gap-3 text-sm text-n-slate-11"
    >
      <Spinner :size="20" />
      <span>{{ t('CONVERSATION_SIDEBAR.ERP.INVOICES.LOADING') }}</span>
    </div>
    <div
      v-else-if="errorCode"
      class="flex min-h-40 flex-col items-center justify-center gap-3 text-center text-sm text-n-slate-11"
    >
      <p class="mb-0">{{ errorMessage }}</p>
      <Button
        data-testid="erp-invoices-retry"
        sm
        faded
        slate
        icon="i-lucide-refresh-ccw"
        :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.RETRY')"
        @click="emit('retry')"
      />
    </div>
    <div v-else class="max-h-[60vh] overflow-y-auto">
      <BaseTable
        :headers="headers"
        :items="invoices"
        :no-data-message="t('CONVERSATION_SIDEBAR.ERP.INVOICES.EMPTY')"
      >
        <template #row="{ items }">
          <template v-for="invoice in items" :key="invoice.external_id">
            <BaseTableRow :item="invoice">
              <template #default="{ item }">
                <BaseTableCell>{{ item.external_id }}</BaseTableCell>
                <BaseTableCell>{{ displayDate(item.issued_on) }}</BaseTableCell>
                <BaseTableCell>{{ displayDate(item.due_on) }}</BaseTableCell>
                <BaseTableCell>{{ displayTotal(item.total) }}</BaseTableCell>
                <BaseTableCell align="end">
                  <Button
                    :data-testid="`erp-invoice-toggle-${item.external_id}`"
                    sm
                    ghost
                    slate
                    :icon="
                      activeInvoiceId === item.external_id
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    :aria-expanded="activeInvoiceId === item.external_id"
                    :aria-label="
                      activeInvoiceId === item.external_id
                        ? t(
                            'CONVERSATION_SIDEBAR.ERP.INVOICES.COLLAPSE_DESCRIPTION',
                            { id: item.external_id }
                          )
                        : t(
                            'CONVERSATION_SIDEBAR.ERP.INVOICES.EXPAND_DESCRIPTION',
                            { id: item.external_id }
                          )
                    "
                    @click="emit('toggleInvoice', item.external_id)"
                  />
                </BaseTableCell>
              </template>
            </BaseTableRow>
            <tr v-if="activeInvoiceId === invoice.external_id">
              <td colspan="5" class="px-4 py-3">
                <div
                  v-if="invoiceDetailsById[invoice.external_id]?.isLoading"
                  class="flex items-center gap-2 text-sm text-n-slate-11"
                >
                  <Spinner :size="16" />
                  <span>
                    {{
                      t('CONVERSATION_SIDEBAR.ERP.INVOICES.LOADING_DESCRIPTION')
                    }}
                  </span>
                </div>
                <div
                  v-else-if="invoiceDetailsById[invoice.external_id]?.errorCode"
                  class="flex items-center justify-between gap-3 text-sm text-n-slate-11"
                >
                  <p class="mb-0">
                    {{
                      errorMessageFor(
                        invoiceDetailsById[invoice.external_id].errorCode
                      )
                    }}
                  </p>
                  <Button
                    :data-testid="`erp-invoice-description-retry-${invoice.external_id}`"
                    sm
                    faded
                    slate
                    icon="i-lucide-refresh-ccw"
                    :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.RETRY')"
                    @click="emit('retryDescription', invoice.external_id)"
                  />
                </div>
                <div
                  v-else-if="
                    invoiceDetailsById[invoice.external_id]?.items?.length
                  "
                  :data-testid="`erp-invoice-description-${invoice.external_id}`"
                  class="overflow-hidden rounded-lg border border-n-weak"
                >
                  <table class="w-full table-fixed text-sm">
                    <thead class="bg-n-alpha-2 text-xs text-n-slate-12">
                      <tr>
                        <th class="px-3 py-2 text-left font-medium">
                          {{
                            t(
                              'CONVERSATION_SIDEBAR.ERP.INVOICES.DESCRIPTION_LABEL'
                            )
                          }}
                        </th>
                        <th class="w-28 px-3 py-2 text-right font-medium">
                          {{ t('CONVERSATION_SIDEBAR.ERP.INVOICES.AMOUNT') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak">
                      <tr
                        v-for="(item, index) in invoiceDetailsById[
                          invoice.external_id
                        ].items"
                        :key="`${item.description}-${item.amount}-${index}`"
                      >
                        <td
                          class="whitespace-pre-wrap break-words px-3 py-2 text-n-slate-11"
                        >
                          {{ item.description }}
                        </td>
                        <td
                          class="px-3 py-2 text-right tabular-nums text-n-slate-12"
                        >
                          {{ displayTotal(item.amount) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p
                  v-else-if="invoiceDetailsById[invoice.external_id]?.loaded"
                  class="mb-0 text-sm text-n-slate-11"
                >
                  {{ t('CONVERSATION_SIDEBAR.ERP.INVOICES.NO_DESCRIPTION') }}
                </p>
              </td>
            </tr>
          </template>
        </template>
      </BaseTable>
    </div>
  </Dialog>
</template>
